import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { URL_PARAMS } from '../../common/AppConstants';
import { useURLParams, titleCase } from '../../common/AppUtils';
import { Box, Typography, CircularProgress, Fade } from '@mui/material';

import Button from '@mui/material/Button';
import Title from '../../components/Title';
import {
  Menu,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  ButtonGroup,
  Tooltip,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { httpRequest } from '../../api/helper';
import TimeTable from '../../components/TimeTable';
import MasterViewModal from '../../components/MasterViewModal';
import { useAccount } from '../../context/AccountContext';
import { useNotification } from '../../context/NotificationContext';
import { API_ERROR_MESSAGE, USER_ROLES } from '../../common/AppConstants';
import * as AppUrls from '../../common/AppUrls';

const TITLE = "TimeTable";
const ITEMS_API_URL = 'time-table/time-table-list/';
const DOWNLOAD_API_URL = 'time-table/export-schedule/';
const NO_DATA_AVAILABLE_STRING = 'Please create a new Time Table !!!';

function preventDefault(event) {
  event.preventDefault();
}
const DOWNLOAD_OPTIONS = [
  {
    label: "TimeTable",
    payload: { data_type: "timetable", data_format: "pdf" }, // default
    file_prefix: "classchronicle_print_",
  },
  {
    label: "Master TimeTable",
    payload: { data_type: "master_timetable", data_format: "pdf" },
    file_prefix: "classchronicle_master_print_",
  },
  {
    label: "Staff workload",
    payload: { data_type: "stats", data_format: "pdf" },
    file_prefix: "classchronicle_stats_",
  },
  {
    label: "TimeTable (Excel)",
    payload: { data_type: "timetable", data_format: "excel" },
    file_prefix: "classchronicle_excel_",
  },
];


export default function Schedule() {
  const { addNotification } = useNotification();
  const navigate = useNavigate();
  const { account } = useAccount();

  const [isLoading, setIsLoading] = useState(true);

  const { urlParam, updateUrlParam } = useURLParams();
  const [scheduleId, setScheduleId] = useState(() => urlParam.get(URL_PARAMS.SCHEDULE_ID) || null);
  
  const [scheduleList, setScheduleList] = useState([]);

  const [downloadAnchorEl, setDownloadAnchorEl] = React.useState(null);
  const [downloadSelectedIndex, setDownloadSelectedIndex] = React.useState(0); // default first option
  const downloadOpen = Boolean(downloadAnchorEl);
  const [downloading, setDownloading] = React.useState(false);

  const [manageTTAnchorEl, setManageTTAnchorEl] = React.useState(null);
  const [manageTTSelected, setManageTTSelected] = React.useState("new_schedule"); // default first option
  const manageTTOpen = Boolean(manageTTAnchorEl);

  const [isMasterModalOpen, setIsMasterModalOpen] = useState(false);
  // const [redirecting, setRedirecting] = React.useState(false);

  useEffect(() => {
    updateUrlParam(URL_PARAMS.SCHEDULE_ID, scheduleId);
  }, [scheduleId]);

  const handleScheduleIdChange = (value) => {
    setScheduleId(value);
  };

  const handleMasterViewClick = () => {
    setIsMasterModalOpen(true);
  };

  const handleMasterModalClose = () => {
    setIsMasterModalOpen(false);
  };

  const refreshData = async (event) => {
    setIsLoading(true);

    await httpRequest({
      method: 'GET', 
      url: ITEMS_API_URL,
      onSuccess: (data) => {
        setScheduleList(data.rows);
        if (scheduleId === null && data.rows && data.rows.length > 0) {
          setScheduleId(data.rows[0].id);
        }
      },
      onFailure: (data) => {
        setScheduleList([]);
        setScheduleId(null);
      },
      addNotification,
    });

    setIsLoading(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      refreshData();
    };
    fetchData();
  }, []);

  const handleScheduleCreate = async () => {
    setManageTTSelected("new_schedule");
    handleManageTT();
  };

  const handleManageTT = async () => {
    addNotification('Taking you to timetable management page.', 'info');
    if (manageTTSelected === "new_schedule") {
      navigate(AppUrls.CREATE_TIME_TABLE_URL);
    } else if (scheduleId) {
      navigate(`${AppUrls.CREATE_TIME_TABLE_URL}?tt=${scheduleId}&st=2`);
    } else {
      addNotification('Schedule missing, please create a new schedule', 'error');
    }
  };

  const handleDownload = async (option) => {
    setDownloading(true);

    let payload = option.payload;
    payload["tt"] = scheduleId;

    await httpRequest({
      method: 'GET',
      url: DOWNLOAD_API_URL,
      data: payload,
      config: {
        responseType: 'blob',
      },
      onSuccess: (data) => {
        // Create a Blob (binary large object) from the response data
        const zipFileBlob = new Blob([data], {
          type: 'application/zip',
        });
        // Generate a temporary URL for the Blob
        const blobUrl = window.URL.createObjectURL(zipFileBlob);
        // Create a hidden anchor (<a>) element for downloading the file
        const downloadLink = document.createElement('a');
        downloadLink.href = blobUrl;
        downloadLink.download = `${option.file_prefix}${scheduleId}.zip`; // Set the filename for the download
        // Append the anchor element to the document, trigger the download, then remove it
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
      },
      addNotification,
    });
    
    setDownloading(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 64px)', // viewport height - margings - app bar
      }}
    >
      {/* Title and Button (fixed) */}
      <Box
        sx={{
          flexShrink: 0, // Top level space distribution
          display: 'flex',
          justifyContent: 'space-between',
          mt: 1,
          mb: 1,
        }}
      >
        <Title>{TITLE}</Title>

        {/* Right Side */}
        {!isLoading && scheduleId && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
            }}
          >
            <FormControl sx={{ flex: 1}}>
              <InputLabel id="schedule_version_label">Select</InputLabel>
              <Select
                labelId="schedule_version_label"
                id="schedule_version"
                value={scheduleId}
                label="Select"
                onChange={(e) => handleScheduleIdChange(e.target.value)}
              >
                {scheduleList.map((option_item) => (
                  <MenuItem key={option_item.id} value={option_item.id}>
                    {option_item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Master View Button */}
            {account?.user?.current_role?.toLowerCase() === USER_ROLES.ADMIN && (
              <Tooltip title="Master view" arrow>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleMasterViewClick}
                  sx={{
                    display: { xs: "none", xl: "inline-flex" },
                    minWidth: 32,     // prevents extra horizontal stretch
                    padding: 0,       // remove default padding
                    // lineHeight: 1,
                  }}
                >
                  <FullscreenIcon sx={{ fontSize: 32 }}/>
                </Button>
              </Tooltip>
            )}
            
            {/* Download Schedule */}
            <ButtonGroup variant="contained" color="primary">
              {/* Default button */}
              <Button
                disabled={downloading}
                startIcon={<CloudDownloadIcon />}
                onClick={() => handleDownload(DOWNLOAD_OPTIONS[downloadSelectedIndex])}
                sx={{
                  px: 2,
                  '& .MuiButton-startIcon': {
                    margin: { xs: 0, md: '0 8px 0 -4px' }
                  },
                  minWidth: { xs: "auto", md: "64px" },
                }}
              >
                <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                  {downloading ? "Downloading..." : DOWNLOAD_OPTIONS[downloadSelectedIndex].label}
                </Box>
              </Button>

              {/* Dropdown trigger */}
              <Button
                disabled={downloading}
                size="small"
                onClick={(e) => setDownloadAnchorEl(e.currentTarget)}
              >
                <ExpandMoreIcon />
              </Button>
            </ButtonGroup>
            <Menu
              anchorEl={downloadAnchorEl}
              open={downloadOpen}
              onClose={() => setDownloadAnchorEl(null)}
            >
              {DOWNLOAD_OPTIONS.map((option, index) => (
                <MenuItem
                  key={option.label}
                  selected={index === downloadSelectedIndex}
                  onClick={() => {
                    setDownloadSelectedIndex(index);
                    setDownloadAnchorEl(null);
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Menu>

            {/* Create/Manage Schedule */}
            {account?.user?.current_role?.toLowerCase() === USER_ROLES.ADMIN && (
              <ButtonGroup variant="contained" color="primary">
                {/* Default button */}
                <Button
                  // disabled={downloading}
                  startIcon={manageTTSelected === "new_schedule" ? <AddCircleIcon /> : <EditIcon />}
                  onClick={() => handleManageTT()}
                  sx={{
                    px: 2,
                    '& .MuiButton-startIcon': {
                      margin: { xs: 0, md: '0 8px 0 -4px' }
                    },
                    minWidth: { xs: "auto", md: "64px" },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                    {titleCase(manageTTSelected)}
                  </Box>
                </Button>

                {/* Dropdown trigger */}
                <Button
                  // disabled={downloading}
                  size="small"
                  onClick={(e) => setManageTTAnchorEl(e.currentTarget)}
                >
                  <ExpandMoreIcon />
                </Button>
              </ButtonGroup>
            )}
            <Menu
              anchorEl={manageTTAnchorEl}
              open={manageTTOpen}
              onClose={() => setManageTTAnchorEl(null)}
            >
              <MenuItem
                key="new_schedule"
                selected={manageTTSelected === "new_schedule"}
                onClick={() => {
                  setManageTTSelected("new_schedule");
                  setManageTTAnchorEl(null);
                }}
              >
                New Schedule
              </MenuItem>
              <MenuItem
                key="edit_schedule"
                selected={manageTTSelected === "edit_schedule"}
                onClick={() => {
                  setManageTTSelected("edit_schedule");
                  setManageTTAnchorEl(null);
                }}
              >
                Edit Schedule
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Box>

      {/* Scrollable table container */}
      <Box
        sx={{
          flexGrow: 1, // This makes the table container grow and take the available space
          overflowY: 'auto', // Only the table will scroll
          mt: 1,
          minHeight: 200, // Ensures proper space for loader
          // display: 'flex',
          // flexDirection: 'column', // Aligns table to the top when shown
          // alignItems: isLoading ? 'center' : 'stretch', // Center loader, but stretch table
          // justifyContent: isLoading ? 'center' : 'flex-start', // Center loader, start table at top
        }}
      >
        {isLoading ? (
          <Fade in={isLoading} timeout={500}>
            <CircularProgress size={50} thickness={3} />
          </Fade>
        ) : scheduleId ? (
          <Fade in={!isLoading} timeout={500}>
            <TimeTable scheduleId={scheduleId} isEditable={false} stage='view' />
          </Fade>
        ) : (
          <Box
            sx={{
              textAlign: 'center',
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleScheduleCreate}
              sx={{
                mt: 8,
                py: 1.5,
                px: 4,
                // fontWeight: 600,
                fontSize: '1rem',
                boxShadow: 4,
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Create New Schedule
            </Button>
          </Box>
        )}
      </Box>
      
      {/* Master View Modal */}
      <Box>
        <MasterViewModal
          open={isMasterModalOpen}
          scheduleId={scheduleId}
          scheduleName={scheduleList.find(item => item.id === scheduleId)?.name || scheduleId}
          handleClose={handleMasterModalClose}
        />
      </Box>
    </Box>
  );
}
